/**
 * BillingContext - Manages billing state across the application
 * 
 * Provides:
 * - Current billing status (subscription, trial, credits)
 * - Methods to refresh billing data
 * - Computed properties for access control
 */
import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { billingAPI, BillingStatus } from '../services/api';
import { useAuth } from './AuthContext';

interface BillingContextType {
  billingStatus: BillingStatus | null;
  isLoading: boolean;
  error: string | null;
  refreshBilling: () => Promise<void>;
  
  // Computed properties
  isAlwaysFree: boolean;
  canStartAudit: boolean;
  showBillingWarning: boolean;
  warningMessage: string;
  subscriptionActive: boolean;
  creditsRemaining: number;
  trialDaysRemaining: number;
  isInTrial: boolean;
  isInGrace: boolean;
}

const BillingContext = createContext<BillingContextType | undefined>(undefined);

export const useBilling = () => {
  const context = useContext(BillingContext);
  if (context === undefined) {
    throw new Error('useBilling must be used within a BillingProvider');
  }
  return context;
};

interface BillingProviderProps {
  children: ReactNode;
}

export const BillingProvider: React.FC<BillingProviderProps> = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const [billingStatus, setBillingStatus] = useState<BillingStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch billing status
  const refreshBilling = useCallback(async () => {
    // Wait for Django user to be synced (not just Supabase session)
    if (!isAuthenticated || !user) {
      setBillingStatus(null);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const status = await billingAPI.getBillingStatus();
      setBillingStatus(status);
    } catch (err: any) {
      console.error('Error fetching billing status:', err);
      // Don't show error for 403 - user might not have company yet
      if (err?.response?.status !== 403) {
        setError(err.message || 'Failed to load billing status');
      }
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, user]);

  // Load billing status on auth change (when Django user is synced)
  useEffect(() => {
    refreshBilling();
  }, [refreshBilling]);

  // Computed properties
  const isAlwaysFree = billingStatus?.is_always_free ?? false;
  
  const subscriptionActive = 
    isAlwaysFree || 
    billingStatus?.subscription_status === 'active' ||
    billingStatus?.subscription_status === 'trial';

  const creditsRemaining = billingStatus?.audit_credits_balance ?? 0;
  const trialDaysRemaining = billingStatus?.trial_days_remaining ?? 0;
  const isInTrial = billingStatus?.subscription_status === 'trial';
  const isInGrace = billingStatus?.subscription_status === 'grace';

  // Can start audit if:
  // - Always free account, OR
  // - Has active subscription AND (has credits OR trial not used)
  const canStartAudit = 
    isAlwaysFree ||
    (subscriptionActive && (creditsRemaining > 0 || (isInTrial && !billingStatus?.trial_audit_used)));

  // Determine warning message
  let warningMessage = '';
  let showBillingWarning = false;

  if (!isAlwaysFree && billingStatus) {
    if (billingStatus.subscription_status === 'expired') {
      showBillingWarning = true;
      warningMessage = 'Your subscription has expired. Please reactivate to continue using the platform.';
    } else if (isInGrace) {
      showBillingWarning = true;
      warningMessage = 'Your subscription is in a grace period. Please update your payment method to avoid service interruption.';
    } else if (isInTrial && trialDaysRemaining <= 2) {
      showBillingWarning = true;
      warningMessage = `Your trial ends in ${trialDaysRemaining} day${trialDaysRemaining !== 1 ? 's' : ''}. Subscribe now to continue using the platform.`;
    } else if (subscriptionActive && creditsRemaining === 0 && billingStatus?.trial_audit_used) {
      showBillingWarning = true;
      warningMessage = 'You have no audit credits remaining. Purchase more to continue running audits.';
    } else if (subscriptionActive && creditsRemaining > 0 && creditsRemaining <= 2) {
      showBillingWarning = true;
      warningMessage = `You have ${creditsRemaining} audit credit${creditsRemaining !== 1 ? 's' : ''} remaining. Consider purchasing more.`;
    }
  }

  return (
    <BillingContext.Provider
      value={{
        billingStatus,
        isLoading,
        error,
        refreshBilling,
        isAlwaysFree,
        canStartAudit,
        showBillingWarning,
        warningMessage,
        subscriptionActive,
        creditsRemaining,
        trialDaysRemaining,
        isInTrial,
        isInGrace,
      }}
    >
      {children}
    </BillingContext.Provider>
  );
};

